import { useRef, type ReactNode } from 'react';
import Grid from '@mui/material/Grid2';
import { Typography } from '@mui/material';
import LabeledDatePicker from './LabeledDatePicker';
import LabeledTimePicker from './LabeledTimePicker';
import { GRID_SPACING, COL_2 } from '../constants/layout';
import {
  isAnesthesiaWindowOverADay,
  getAnesthesiaWindowIssues,
  type AnesthesiaWindowFields,
} from '../utils/anesthesiaWindow';

export type { AnesthesiaWindowFields as AnesthesiaTimeRangeFields } from '../utils/anesthesiaWindow';

interface AnesthesiaTimeRangeProps {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  /** ISO date 'YYYY-MM-DD'. The start date defaults to it until the biller edits the date. */
  dateOfService: string;
  startDateError?: string;
  startTimeError?: string;
  endDateError?: string;
  endTimeError?: string;
  onChange: (fields: AnesthesiaWindowFields) => void;
  disabled?: boolean;
  /**
   * Feature 043 — independent per-field visibility from practice settings. Each defaults
   * to true so every existing caller is unaffected. The four fields are four separate
   * catalog entries (`anesthesiaStartDate`/`anesthesiaStartTime`/`anesthesiaEndDate`/
   * `anesthesiaEndTime`), so hiding one must not hide the others.
   */
  showStartDate?: boolean;
  showStartTime?: boolean;
  showEndDate?: boolean;
  showEndTime?: boolean;
  /**
   * Stack all four fields one per full-width line instead of pairing each date with its
   * time. For the back-office claim card, whose column is far narrower than the portal's
   * bill-type card — at that width the paired layout squeezes each control to roughly a
   * third and reads badly.
   */
  stacked?: boolean;
  /** Show the military-time hint under each time field. Off in the back office. */
  showTimeHint?: boolean;
  /**
   * Per-field AI suggestion nodes, each rendered directly beneath its own input. Passed in
   * rather than built here so this component stays unaware of the suggestion UI; grouping
   * them in one block under the whole range left the reader guessing which field each
   * belonged to.
   */
  startDateSuggestion?: ReactNode;
  startTimeSuggestion?: ReactNode;
  endDateSuggestion?: ReactNode;
  endTimeSuggestion?: ReactNode;
}

/**
 * The anesthesia window (feature 044): start date + start time, end date + end time. Four
 * fields, each stating its value outright — nothing is derived, and there is no "Cross day"
 * confirmation, because an overnight case is expressed by simply advancing the end date.
 *
 * Defaults cascade Date of Service → start date → end date, and each default keeps tracking
 * its source until the biller edits that field, after which their value is preserved
 * (FR-014). "Touched" is tracked explicitly rather than inferred by comparing values, so a
 * biller who deliberately retypes the same date does not silently lose the edit on the next
 * Date of Service change.
 */
export default function AnesthesiaTimeRange({
  startDate,
  startTime,
  endDate,
  endTime,
  dateOfService,
  startDateError,
  startTimeError,
  endDateError,
  endTimeError,
  onChange,
  disabled,
  showStartDate = true,
  showStartTime = true,
  showEndDate = true,
  showEndTime = true,
  stacked = false,
  showTimeHint = true,
  startDateSuggestion,
  startTimeSuggestion,
  endDateSuggestion,
  endTimeSuggestion,
}: AnesthesiaTimeRangeProps) {
  // Which dates the biller has taken ownership of. Refs, not state: nothing renders from
  // them, so a re-render on change would be noise (Principle 15).
  const startDateTouched = useRef(false);
  const endDateTouched = useRef(false);

  const effectiveStartDate = startDate || (startDateTouched.current ? '' : dateOfService);
  const effectiveEndDate = endDate || (endDateTouched.current ? '' : effectiveStartDate);

  const emit = (next: Partial<AnesthesiaWindowFields>) =>
    onChange({
      anesthesiaStartDate: effectiveStartDate,
      anesthesiaStartTime: startTime,
      anesthesiaEndDate: effectiveEndDate,
      anesthesiaEndTime: endTime,
      ...next,
    });

  const handleStartDateChange = (value: string) => {
    startDateTouched.current = true;
    // An untouched end date follows the start date, so moving the start moves the window.
    emit({
      anesthesiaStartDate: value,
      ...(endDateTouched.current ? {} : { anesthesiaEndDate: value }),
    });
  };

  const handleEndDateChange = (value: string) => {
    endDateTouched.current = true;
    emit({ anesthesiaEndDate: value });
  };

  const currentWindow = {
    anesthesiaStartDate: effectiveStartDate,
    anesthesiaStartTime: startTime,
    anesthesiaEndDate: effectiveEndDate,
    anesthesiaEndTime: endTime,
  };
  const overADay = isAnesthesiaWindowOverADay(currentWindow);

  /**
   * Live validation, computed here so BOTH surfaces get it from one place.
   *
   * The retired "Cross day" checkbox used to carry an in-component check, and deleting the
   * checkbox took that with it — leaving the portal reliant on its zod refinement, which
   * only surfaces on submit, while the back office kept live feedback from its own
   * imperative setError. That asymmetry was an accident, not a decision.
   *
   * An externally-supplied message still wins: the schema and the back-office check are
   * what actually block a save, and their wording should be what the biller reads.
   */
  const liveIssues = getAnesthesiaWindowIssues(currentWindow);
  const liveMessage = (field: keyof AnesthesiaWindowFields) =>
    liveIssues.find((i) => i.field === field)?.message;

  // Stacked: every field spans the row. Paired: a date sits beside its own time, two
  // rows of two — never three-across, which is what COL_2 alone would give.
  const cellSize = stacked ? 12 : COL_2;
  const startDateMsg = startDateError ?? liveMessage('anesthesiaStartDate');
  const startTimeMsg = startTimeError ?? liveMessage('anesthesiaStartTime');
  const endDateMsg = endDateError ?? liveMessage('anesthesiaEndDate');
  const endTimeMsg = endTimeError ?? liveMessage('anesthesiaEndTime');
  const startPair = (
    <>
      {showStartDate && (
        <Grid size={cellSize}>
          <LabeledDatePicker
            label="Anesthesia Start Date"
            value={effectiveStartDate}
            onChange={handleStartDateChange}
            error={!!startDateMsg}
            helperText={startDateMsg}
            disabled={disabled}
          />
          {startDateSuggestion}
        </Grid>
      )}
      {showStartTime && (
        <Grid size={cellSize}>
          <LabeledTimePicker
            label="Anesthesia Start Time"
            showHint={showTimeHint}
            value={startTime}
            onChange={(value) => emit({ anesthesiaStartTime: value })}
            error={!!startTimeMsg}
            helperText={startTimeMsg}
            disabled={disabled}
          />
          {startTimeSuggestion}
        </Grid>
      )}
    </>
  );
  const endPair = (
    <>
      {showEndDate && (
        <Grid size={cellSize}>
          <LabeledDatePicker
            label="Anesthesia End Date"
            value={effectiveEndDate}
            onChange={handleEndDateChange}
            error={!!endDateMsg}
            helperText={endDateMsg}
            disabled={disabled}
          />
          {endDateSuggestion}
        </Grid>
      )}
      {showEndTime && (
        <Grid size={cellSize}>
          <LabeledTimePicker
            label="Anesthesia End Time"
            showHint={showTimeHint}
            value={endTime}
            onChange={(value) => emit({ anesthesiaEndTime: value })}
            error={!!endTimeMsg}
            helperText={endTimeMsg}
            disabled={disabled}
          />
          {endTimeSuggestion}
        </Grid>
      )}
    </>
  );

  return (
    <>
      {stacked ? (
        // One container: full-width cells wrap naturally, one field per line.
        <Grid container columnSpacing={GRID_SPACING} rowSpacing={0.5} sx={{ mb: 1 }}>
          {startPair}
          {endPair}
        </Grid>
      ) : (
        <>
          {/* One row per endpoint, so a date always sits beside its own time. */}
          <Grid container columnSpacing={GRID_SPACING} rowSpacing={0.5} sx={{ mb: 1 }}>
            {startPair}
          </Grid>
          <Grid container columnSpacing={GRID_SPACING} rowSpacing={0.5} sx={{ mb: 1 }}>
            {endPair}
          </Grid>
        </>
      )}
      {/* A window over a day is accepted, not blocked — surfaced for review (FR-020). */}
      {overADay && (
        <Typography variant="body2" sx={{ fontSize: 13, color: 'warning.main', mb: 1 }}>
          This anesthesia window is longer than 24 hours — please confirm the dates.
        </Typography>
      )}
    </>
  );
}
